import { FC, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getContentById, updateContent, Content, QuizData } from '@/services/university-service';
import { Spinner, Center, VStack, useToast, Box, Button } from '@chakra-ui/react';
import { ADMIN_ROUTES } from '@bemap/utils';
import { QuizBuilder } from '@bemap/university-microfrontends';


const EditQuizContentPage: FC = () => {
  const { contentId } = useParams<{ courseId: string; moduleId: string; contentId: string }>();
  const [content, setContent] = useState<Content | null>(null);
  const [loading, setLoading] = useState(true);
  const [quizData, setQuizData] = useState<QuizData | null>(null);
  const [saving, setSaving] = useState(false);
  const toast = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    const loadContent = async () => {
      setLoading(true);
      try {
        const response = await getContentById(Number(contentId));
        if (response.statusCode === 200 && response.message) {
          const c = response.message as Content;
          setContent(c);
          setQuizData((c.data as QuizData) || null);
        } else {
          throw new Error(response.message || 'Failed to load content');
        }
      } catch (error: any) {
        toast({ title: 'Error', description: error.message, status: 'error' });
        navigate(ADMIN_ROUTES.UNIVERSITY_COURSES);
      } finally {
        setLoading(false);
      }
    };
    loadContent();
  }, [contentId, toast, navigate]);

  const handleSave = async () => {
    if (!content || !quizData) return;
    setSaving(true);
    try {
      const payload = { data: quizData };
      const response = await updateContent(content.id, payload);
      if (response.statusCode === 200) {
        toast({ title: 'Saved', status: 'success' });
        navigate(-1);
      } else {
        throw new Error(response.message || 'Failed to save');
      }
    } catch (error: any) {
      toast({ title: 'Error', description: error.message, status: 'error' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Center h="50vh"><Spinner /></Center>;
  if (!content || !quizData) return null;

  return (
    <VStack spacing={6} align="stretch" maxW="4xl" mx="auto" mt={8}>
      <Box>
        <QuizBuilder value={quizData} onChange={setQuizData} onSave={handleSave} isSubmitting={saving} />
      </Box>
      <Button colorScheme="blue" onClick={handleSave} isLoading={saving}>Save</Button>
    </VStack>
  );
};

export default EditQuizContentPage; 